import { useEffect, useState, type SyntheticEvent } from "react";
import { Loader2, Trash2 } from "lucide-react";

const OPEN_GUARD_MS = 480;

type Props = {
  open: boolean;
  title: string;
  message?: string;
  confirmLabel?: string;
  cancelLabel?: string;
  busy?: boolean;
  onConfirm: () => void;
  onCancel: () => void;
};

/** Confirmation stylisée — remplace window.confirm. */
export function ChatConfirmDialog({
  open,
  title,
  message,
  confirmLabel = "Supprimer",
  cancelLabel = "Annuler",
  busy = false,
  onConfirm,
  onCancel,
}: Props) {
  const [armed, setArmed] = useState(false);

  useEffect(() => {
    if (!open) {
      setArmed(false);
      return;
    }
    setArmed(false);
    // Ignore le clic fantôme post appui-long / menu (sinon la modale se referme toute seule)
    const t = window.setTimeout(() => setArmed(true), OPEN_GUARD_MS);
    return () => window.clearTimeout(t);
  }, [open]);

  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key !== "Escape" || busy || !armed) return;
      onCancel();
    };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [open, busy, armed, onCancel]);

  if (!open) return null;

  const dismissOverlay = (e: SyntheticEvent) => {
    e.preventDefault();
    e.stopPropagation();
    if (!armed || busy) return;
    onCancel();
  };

  const runCancel = (e: SyntheticEvent) => {
    e.preventDefault();
    e.stopPropagation();
    if (!armed || busy) return;
    onCancel();
  };

  const runConfirm = (e: SyntheticEvent) => {
    e.preventDefault();
    e.stopPropagation();
    if (!armed || busy) return;
    onConfirm();
  };

  return (
    <div
      className="chat-confirm-overlay fixed inset-0 z-[90] flex items-center justify-center p-4"
      role="presentation"
      onPointerDown={(e) => {
        // Absorbe le ghost click sans fermer tant que non armé
        if (e.target === e.currentTarget) {
          e.preventDefault();
          e.stopPropagation();
        }
      }}
      onClick={dismissOverlay}
    >
      <div
        className="chat-confirm-dialog w-full max-w-sm overflow-hidden rounded-2xl border border-[var(--chat-border)] bg-[var(--chat-surface)] shadow-[var(--chat-shadow)]"
        role="alertdialog"
        aria-modal="true"
        aria-labelledby="chat-confirm-title"
        onPointerDown={(e) => e.stopPropagation()}
        onClick={(e) => e.stopPropagation()}
      >
        <div className="px-5 py-5">
          <div className="flex items-start gap-3">
            <span className="flex size-11 shrink-0 items-center justify-center rounded-full bg-[var(--chat-danger)]/12 text-[var(--chat-danger)]">
              <Trash2 className="size-5" aria-hidden />
            </span>
            <div className="min-w-0 pt-0.5">
              <h2
                id="chat-confirm-title"
                className="text-base font-semibold tracking-tight text-[var(--chat-text)]"
              >
                {title}
              </h2>
              {message ? (
                <p className="mt-1.5 text-sm leading-relaxed text-[var(--chat-muted)]">{message}</p>
              ) : null}
            </div>
          </div>

          <div className="mt-6 flex flex-col gap-2 sm:flex-row sm:justify-end">
            <button
              type="button"
              onPointerUp={runCancel}
              onClick={runCancel}
              disabled={busy || !armed}
              className="rounded-xl px-4 py-2.5 text-sm font-medium text-[var(--chat-muted)] transition hover:bg-[var(--chat-surface-elevated)] disabled:opacity-50"
            >
              {cancelLabel}
            </button>
            <button
              type="button"
              onPointerUp={runConfirm}
              onClick={runConfirm}
              disabled={busy || !armed}
              className="inline-flex items-center justify-center gap-2 rounded-xl bg-[var(--chat-danger)] px-4 py-2.5 text-sm font-semibold text-white shadow-sm transition hover:brightness-110 disabled:opacity-50"
            >
              {busy ? <Loader2 className="size-4 animate-spin" aria-hidden /> : null}
              {confirmLabel}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
